import { useMemo, type RefObject, type ChangeEvent } from 'react'
import { HStack, IconButton, Button, Tooltip, Spinner } from '@chakra-ui/react'
import { Plus, Download, Upload, FileDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAccessCheck } from '@/hooks/useAccess'

interface ActionButtonsProps {
  pathname?: string
  onExport?: () => void
  onImport?: () => void
  onDownloadTemplate?: () => void
  onCreate?: () => void
  createRoute?: string
  createLabel?: string
  isExporting?: boolean
  isImporting?: boolean
  showDownloadTemplate?: boolean
  fileInputRef?: RefObject<HTMLInputElement>
  onFileUpload?: (event: ChangeEvent<HTMLInputElement>) => void
  acceptFileTypes?: string
}

export function ActionButtons({
  pathname,
  onExport,
  onImport,
  onDownloadTemplate,
  onCreate,
  createRoute,
  createLabel = 'Create New',
  isExporting = false,
  isImporting = false,
  showDownloadTemplate = true,
  fileInputRef,
  onFileUpload,
  acceptFileTypes = '.csv',
}: ActionButtonsProps) {
  const navigate = useNavigate()
  const access = useAccessCheck(pathname)

  const computedCreateRoute = useMemo(() => {
    if (createRoute) return createRoute
    const currentPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '')
    return `${currentPath.replace(/\/$/, '')}/create`
  }, [createRoute, pathname])

  if (!access.can_create && !access.can_export && !access.can_import) {
    return null
  }

  const handleCreate = () => {
    if (onCreate) {
      onCreate()
    } else {
      navigate(computedCreateRoute)
    }
  }

  const handleImport = () => {
    if (onImport) {
      onImport()
    } else if (fileInputRef?.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <>
      <HStack spacing={2}>
        {access.can_export && onExport && (
          <>
            <Tooltip label="Export Data" placement="top">
              <IconButton
                aria-label="Export Data"
                size="sm"
                colorScheme="blue"
                variant="outline"
                icon={isExporting ? <Spinner size="sm" /> : <FileDown size={16} />}
                onClick={onExport}
                isDisabled={isExporting}
              />
            </Tooltip>
            {showDownloadTemplate && onDownloadTemplate && (
              <Tooltip label="Download Template" placement="top">
                <IconButton
                  aria-label="Download Template"
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  icon={<Download size={16} />}
                  onClick={onDownloadTemplate}
                />
              </Tooltip>
            )}
          </>
        )}
        {access.can_import && (onImport || fileInputRef) && (
          <Tooltip label="Import Data" placement="top">
            <IconButton
              aria-label="Import Data"
              size="sm"
              colorScheme="green"
              variant="outline"
              icon={isImporting ? <Spinner size="sm" /> : <Upload size={16} />}
              onClick={handleImport}
              isDisabled={isImporting}
            />
          </Tooltip>
        )}
        {access.can_create && (
          <Button size="sm" colorScheme="blue" leftIcon={<Plus size={16} />} onClick={handleCreate}>
            {createLabel}
          </Button>
        )}
      </HStack>
      {access.can_import && fileInputRef && onFileUpload && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileUpload}
          accept={acceptFileTypes}
          style={{ display: 'none' }}
        />
      )}
    </>
  )
}


